// FLUENT'S TEXT CONTROLS, NAMED BY THEIR FIELD'S VISIBLE LABEL (QA 2026-10-04,
// A-08).
//
// A Fluent `<Field label>` names its control with `<label htmlFor>` alone: for
// an input it reports `supportsLabelFor`, so it sets no `aria-labelledby`.
// Under the browser's naming rules that label already outranks a placeholder,
// so this is not a Fluent fault and no screen-reader problem is known. But the
// accessibility-tree reader the QA browser tool uses ranks the placeholder
// first, so there the Rules and References dialogs read as "e.g. xerox" or
// "e.g. 214" instead of "When an invoice arrives from" or "Veh #". These
// wrappers point `aria-labelledby` at that same label and, once it has
// rendered, copy its visible words into `aria-label` as well — in that reader
// only `aria-label` beats the placeholder. That is the move
// `EntityPicker`'s `label` already makes (QA 2026-09-30, C1-05). Same words
// either way, the required-field asterisk left out because Fluent marks it
// `aria-hidden`.
//
// A no-op outside a `Field`, inside a `Field` with no label, and wherever the
// caller names the control itself (`aria-label` / `aria-labelledby`), so a
// screen can use them for every control without checking which is which.
// `EntityPicker` uses the same hook (`useFieldLabelName`, `lib/hooks.ts`).
import { forwardRef } from 'react'
import {
  Combobox,
  Input,
  Textarea,
  type ComboboxProps,
  type InputProps,
  type TextareaProps,
} from '@fluentui/react-components'
import { useFieldLabelName } from '../lib/hooks'

export const FieldInput = forwardRef<HTMLInputElement, InputProps>(function FieldInput(props, ref) {
  const named = useFieldLabelName(props)
  return <Input ref={ref} {...props} {...named} />
})

export const FieldTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function FieldTextarea(props, ref) {
  const named = useFieldLabelName(props)
  return <Textarea ref={ref} {...props} {...named} />
})

export const FieldCombobox = forwardRef<HTMLInputElement, ComboboxProps>(function FieldCombobox(props, ref) {
  const named = useFieldLabelName(props)
  return <Combobox ref={ref} {...props} {...named} />
})
